"use client";

import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  FeatureFlag as GeneratedFeatureFlag,
  FeatureFlagExperiment,
  fetchEvaluateFeatureFlags,
} from "@justhome/api-core";
import { useCallbackRef } from "../hooks/useCallbackRef";
import logger from "../logger";

type FeatureFlag = Omit<GeneratedFeatureFlag, "value"> & { value: unknown };

type FeatureFlagsContextValue = {
  features: Record<string, FeatureFlag>;
  ready: boolean;
  trackExperiment: (experiment: FeatureFlagExperiment) => void;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  features: {},
  ready: false,
  trackExperiment: () => {},
});

type FeatureFlagsProviderProps = PropsWithChildren<{
  url: string;
  leadId?: string;
  partnerId?: string;
  email?: string;
  onExperimentViewed?: (experimentKey: string, variationId: number) => void;
}>;

export const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({
  url,
  leadId,
  partnerId,
  email,
  onExperimentViewed,
  children,
}) => {
  const [loaded, setLoaded] = useState<{
    requestKey: string | null;
    features: Record<string, FeatureFlag>;
  }>({ requestKey: null, features: {} });
  const trackedExperiments = useRef(new Set<string>());
  const experimentViewed = useCallbackRef(onExperimentViewed);
  const pageUrl = url.replace(/#.*$/, "");
  const requestKey = JSON.stringify([pageUrl, leadId, partnerId, email]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let features: Record<string, FeatureFlag> = {};

      try {
        const response = await fetchEvaluateFeatureFlags({
          body: {
            url: new URL(pageUrl, window.location.origin).href,
            leadId,
            partnerId,
            email,
          },
        });
        features = response.features;
      } catch (error) {
        logger.error("Failed to load feature flags", error);
      }

      if (!cancelled) {
        setLoaded({ requestKey, features });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [requestKey, pageUrl, leadId, partnerId, email]);

  const trackExperiment = useCallback(
    (experiment: FeatureFlagExperiment) => {
      const trackingKey = `${experiment.key}:${experiment.variationId}`;
      if (trackedExperiments.current.has(trackingKey)) {
        return;
      }

      trackedExperiments.current.add(trackingKey);
      experimentViewed(experiment.key, experiment.variationId);
    },
    [experimentViewed],
  );

  return (
    <FeatureFlagsContext.Provider
      value={{
        features: loaded.features,
        ready: loaded.requestKey === requestKey,
        trackExperiment,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const useFeatureFlag = (key: string) => {
  const { features, trackExperiment } = useContext(FeatureFlagsContext);
  const flag = features[key];

  useEffect(() => {
    if (flag?.experiment) {
      trackExperiment(flag.experiment);
    }
  }, [flag, trackExperiment]);

  return flag;
};

export const useFeatureValue = <T,>(key: string, fallback: T): T => {
  const flag = useFeatureFlag(key);

  return (flag?.value as T | null | undefined) ?? fallback;
};

export const useFeatureIsOn = (key: string): boolean => {
  const flag = useFeatureFlag(key);

  return Boolean(flag?.value);
};

export const FeaturesReady: FC<{
  children: ReactNode;
  fallback?: ReactNode;
  timeout?: number;
}> = ({ children, fallback = null, timeout }) => {
  const { ready } = useContext(FeatureFlagsContext);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (ready || timeout === undefined) {
      return;
    }

    const timer = window.setTimeout(() => setTimedOut(true), timeout);

    return () => window.clearTimeout(timer);
  }, [ready, timeout]);

  return <>{ready || timedOut ? children : fallback}</>;
};
